import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { ImageAskTool } from 'js/src/agent/tools/ImageAskTool';
import { ListSceneDesignTool } from 'js/src/agent/tools/ListSceneDesignTool';
import { SetCaptionsTool } from 'js/src/agent/tools/SetCaptionsTool';
import { SetTextTool } from 'js/src/agent/tools/SetTextTool';

export const createHowToVideoSkill: Skill = {
    name: SKILL_NAMES.createHowToVideo,
    description: 'Instructional how-to video teaching a process step by step.',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        title: 'How-to video',
        blurb: 'Teach a process one clear step at a time.',
        icon: 'school',
        gradient: ['#5589A0', '#6485A8'],
    },
    get instructions(): string {
        return `# Create How-To Video

The instructional format: a process taught step by step, narrated by an AI
voiceover, each step announced and then demonstrated.

Load the \`${SKILL_NAMES.createVideo}\` skill too — everything there applies; this skill adds the
step-by-step formula, and its rules win where they conflict.

## Tone — guiding and clear, not salesy
Write like a patient, competent teacher: clear, encouraging, precise. Every
sentence moves the process forward or clarifies it — no marketing language. Steps
use imperative phrasing ("Start by...", "Next, ...", "Once that's done, ..."),
instructions to follow rather than a story being told.

## Structure — as many steps as the process actually needs
The step count is dictated by the real process — never forced to a round number.
- Use the user's steps as given, in their existing order. Otherwise break the
  process down with them and confirm the breakdown before generating — a wrong or
  muddled step actively misleads the viewer.
- Steps stay in strict chronological/logical order: never reorder or skip for
  pacing. Combining two trivial steps is legitimate; reordering for drama is not.
- Each step gets a beat pair covering its words in the transcript:
  1. **Step overlay** (no media): the step number and a short, clear instruction
     headline.
  2. **Demonstration overlay** (media): the step actually being done — footage
     that shows that specific action as literally as possible, not generic topic
     footage.
- Never span a step announcement and its demonstration in one overlay's word
  range, and never combine two steps. A longer instruction can split across
  overlays at natural clause breaks.
- Consider an optional "what you'll need" beat before Step 1 when the process
  requires materials or prerequisites — stated plainly, once.

## Numbering
- Every Step overlay shows its step number as a real, prominent element — it's
  structural chrome, exempt from the spoken-words rule (its own ${SetTextTool.toolName} call,
  needn't be a spoken word).
- Numbers run 1, 2, 3, ... matching the real step count — no skips or repeats,
  one consistent labeling style throughout.

## Design selection — filter, then pick
1. Step overlays: only designs with a clear slot for the step number (check the
   actual asset list from ${ListSceneDesignTool.toolName}, not the name).
2. Then by media: Step overlay designs have no media asset; Demonstration overlay
   designs have one.
3. Rotate the survivors across steps — vary Step designs and Demonstration designs
   separately. Never a caption-style design for either beat.
4. Nothing satisfies both filters → flag it; don't drop the number.

## Script structure
- Target roughly 45 seconds of narration total, whatever the step count — per-step
  detail scales down as steps go up. If 45 seconds genuinely can't cover the real
  process, confirm with the user which steps to combine or trim.
- Open with a hook naming what's being taught (the title card, "title" design);
  write step by step in strict order; close by confirming the process is complete
  ("And that's it — ...") plus an optional topic-specific next step, on an "outro"
  design.
- **Captions are OFF for this format** — on-screen text (including step numbers)
  does that job. Call ${SetCaptionsTool.toolName} with enabled: false right after generating the
  voiceover.

## Review
Load the \`${SKILL_NAMES.reviewVideo}\` skill before the review pass.
Additional checks:
- Every step has its Step overlay then its Demonstration overlay — nothing
  skipped, merged, or duplicated; numbers sequential and consistent.
- Every Step overlay visibly shows its number — check with ${ImageAskTool.toolName}.
- Tally the media/no-media split overlay by overlay; confirm both design rotations
  vary; confirm the title-card open, outro close, and captions off.
- Check pacing by reading the breaks: every overlay ends at a natural clause
  boundary — no phrase split awkwardly, no overlay cramming two thoughts.`;
    },
};
